import { createSelector } from 'reselect';
import { checkAccessBasedOnAssignmentPermissions, assignmentTypes, assignmentPermissions } from '../../shared/authorization';
import { selectSlGroupType, selectSlGroupItem, selectCountryTaxType } from '../aggregationMenu/store/selectors';
import { AGGREGATION_DROPLIST_ITEMS, COMPANY_CODE_TYPE_VALUE } from '../aggregationMenu/constants';
import { decodeCountryTaxType } from '../../utils/encodeDecodeCTT';
import { countryTaxTypes, countryTaxTypesViewOnlyOptions } from '../../shared/constants';

export const countryTaxTypeSelector = state => state.balanceSheetManagement.shared.countryTaxType;

export const allowChoosingFbosSelector = createSelector(
  countryTaxTypeSelector,
  countryTaxType => countryTaxType && countryTaxType.value === countryTaxTypes.US2
);

export const accountsSelector = state => state.balanceSheetManagement.shared.accounts;

export const countryTaxTypesOptionsSelector = createSelector(
  state => state.balanceSheetManagement.shared.countryTaxTypes,
  countryTaxTypes => countryTaxTypes.map(({ description, countryTaxType, currencyCode }) => ({
    value: countryTaxType,
    label: description,
    currencyCode,
  }))
);

export const isLoadingCountryTaxTypesSelector = state => state.balanceSheetManagement.shared.isLoadingCountryTaxTypes;

export const taxCurrencySelector = createSelector(
  [
    state => state.balanceSheetManagement.shared.countryTaxType,
    state => state.balanceSheetManagement.shared.countryTaxTypes,
  ],
  (countryTaxType, countryTaxTypes) => {
    if (!countryTaxType) {
      return null;
    }

    const countryTaxTypeToReturn = countryTaxTypes.find(
      taxType => taxType.VALUE === countryTaxType.value);

    return countryTaxTypeToReturn ? countryTaxTypeToReturn.CURRENCY_CODE : null;
  }
);

export const isTaxEntriesLoadingSelector = state => state.balanceSheetManagement.shared.taxEntriesLoading;

export const isSubmittingTaxEntrySelector = state => state.balanceSheetManagement.shared.submittingTaxEntry;

export const isLoadingNotificationsSelector = state => state.balanceSheetManagement.shared.notificationsLoading;

export const diagnosticsLoadingSelector = state => state.balanceSheetManagement.shared.diagnosticsLoading;

export const tbeErrorsSelector = state => state.balanceSheetManagement.shared.tbeErrors;
export const isLoadingTbeErrorsSelector = state => state.balanceSheetManagement.shared.isLoadingTbeErrors;

export const diagnosticsSelector = createSelector(
  state => state.balanceSheetManagement.shared.diagnostics,
  diagnostics => diagnostics && ({
    ...diagnostics,
    nonMatchingBalancesSupport: diagnostics.nonMatchingBalancesSupport
      && diagnostics.nonMatchingBalancesSupport.map(balance => ({
        ...balance,
        ...decodeCountryTaxType(balance.countryTaxType),
      })),
  })
);

export const tbeErrorObjSelector = createSelector(
  selectSlGroupType,
  selectSlGroupItem,
  tbeErrorsSelector,
  (groupType, groupItem, tbeErrorList) =>
    groupType && groupType.value === COMPANY_CODE_TYPE_VALUE && groupItem && tbeErrorList
      ? (tbeErrorList.find(tbeError => tbeError.companyCode === groupItem.value) || {}) : {}
);

export const isAllowedToMakeDirectEntriesSelectorFactory = ({ checkAggregationDroplistValue = false } = {}) =>
  createSelector(
    selectCountryTaxType,
    selectSlGroupType,
    selectSlGroupItem,
    ({ balanceSheetManagement }) =>
      balanceSheetManagement.deferredRollforward.aggregationDroplistValue,
    ({ user }) => user.user,
    tbeErrorObjSelector,
    (countryTaxType, groupType, groupItem, aggregationDroplistValue, user, tbeErrorValue ) => {
      if (checkAggregationDroplistValue && aggregationDroplistValue !== AGGREGATION_DROPLIST_ITEMS.ACCOUNT) {
        return false;
      }
      if (!groupType
        || groupType.value !== COMPANY_CODE_TYPE_VALUE
        || !groupItem
        || !countryTaxType
        || countryTaxTypesViewOnlyOptions[countryTaxType.value]
        || tbeErrorValue.tbeError ) {
        return false;
      }

      return checkAccessBasedOnAssignmentPermissions({
        user,
        withAssignments: [{
          assignmentType: assignmentTypes.CC,
          requiredPermission: assignmentPermissions.SubledgerAdd,
        }],
        companyCode: groupItem.value,
      });
    }
  );

export const subledgerGlAccountsSelector = createSelector(
  ({ balanceSheetManagement }) => balanceSheetManagement.shared.subledgerGlAccounts,
  (state, { formOptions }) => formOptions.glAccountList,
  (subledgerGlAccounts, glAccountList) => subledgerGlAccounts && subledgerGlAccounts.length
    ? subledgerGlAccounts.filter(({ listName }) => listName === glAccountList)
      .map(({ description, glAccount }) => ({
        label: `${glAccount} - ${description}`,
        value: glAccount,
      }))
    : []
);

export const taxEntriesToImportSelector = state =>
  state.balanceSheetManagement.shared.batchTaxEntries.taxEntriesToImport;

export const invalidTaxEntriesFoundSelector = state =>
  state.balanceSheetManagement.shared.batchTaxEntries.taxEntriesToImport.some(
    taxEntry => taxEntry.validationError
  );

export const areImportsPendingSelector = createSelector(
  taxEntriesToImportSelector,
  invalidTaxEntriesFoundSelector,
  (taxEntriesToImport, invalidTaxEntriesFound) => taxEntriesToImport.length !== 0 && !invalidTaxEntriesFound
);

export const taxEntriesTreeSelector = state => state.balanceSheetManagement.shared.taxEntriesTree;
export const isLoadingTaxEntriesTreeSelector = state => state.balanceSheetManagement.shared.isLoadingTaxEntriesTree;

export const deferredTaxRateSelector = state => state.balanceSheetManagement.shared.deferredTaxRate;

export const apportionmentRateSelector = state => state.balanceSheetManagement.shared.stateApportionmentRate;
export const isLoadingObligationRatesSelector = state => state.balanceSheetManagement.shared.isLoadingObligationRates;

export const typeDescriptionsSelector = state => state.balanceSheetManagement.shared.typeDescriptions;
export const isLoadingTypeDescriptionsSelector = state => state.balanceSheetManagement.shared.isLoadingTypeDescriptions;

export const activityDescriptionsSelector = state => state.balanceSheetManagement.shared.activityDescriptions;
export const isLoadingActivityDescriptionsSelector = state =>
  state.balanceSheetManagement.shared.isLoadingActivityDescriptions;

export const proposedJournalsSelector = state => state.balanceSheetManagement.shared.proposedJournals;
export const proposedJournalsForReclassSelector = state =>
  state.balanceSheetManagement.shared.proposedJournalsForReclass;
export const isFetchingProposedJournalsSelector = state =>
  state.balanceSheetManagement.shared.fetchingProposedJournals
  || state.balanceSheetManagement.shared.fetchingProposedReclassJournals;

